
import { observer } from "mobx-react";
import {
  Typography,
  useTheme,
  IconButton,
  Grid,
  Box,
  Menu,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FileCopy, MoreVert } from '@material-ui/icons';
import { DataGrid, GridColumnProps, Button, StandardDialog, SelectField, SubmitMenuItem, MenuItem } from '@roc/ui';
import React, { useEffect, useState } from "react";
import { useStore } from '@roc/client-portal-shared/hooks';
import { businessUnitOptions } from "../leadsDashboard/allLeadDashboard";
import { isLeadDashboard } from "../leads/listViews/leadsDashboardGrid";
import { LEAD_OBJECT_TYPE } from "../leads/constants/constants";
import { SelectCadenceListStepper } from "./selectCadenceListStepper";


const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: '0px',
  },
  peopleContainer: {
    minHeight: "400px",
    maxHeight: "500px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    gap: "10px",
    padding: "10px",
    backgroundColor: "white",
    borderRadius: "8px",
    marginTop: theme.spacing(5),
  },
  noStepsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    marginTop: theme.spacing(10),
  },
  associateButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
}));

export const PeopleCadenceDetailsTabV2 = observer(({ gritStore }) => {
  const { salesforceLeadStore, cadenceStore, globalStore } = useStore();
  const [openAddContactsModal, setOpenAddContactsModal] = useState(false);
  const [column, setColumn] = useState([]);
  const classes = useStyles();
  const { cadence } = cadenceStore
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const getColumns = () => {
    const baseColumns: GridColumnProps[] = [
      { field: 'name', headerName: 'Name', minWidth: 120 },
      { field: 'email', headerName: 'Email', minWidth: 120 },
      { field: 'phone', headerName: 'Phone Number', minWidth: 120 },
      {
        field: 'journeyBuilderStatus',
        headerName: 'Journey Status',
        minWidth: 120,
        valueFormatter: (params) => {
          return params.value != null ? params.value : "Not in Journey";
        }
      },
      { field: 'actions', headerName: 'Actions', minWidth: 120, cellRenderer: 'actionCellRenderer', }
    ];

    setColumn(baseColumns);
  };

  useEffect(() => {
    getColumns()
    cadenceStore.gridBusinessUnit = globalStore.lenderInfo?.businessUnit;
    if (cadence?.metrics?.totalProspects > 0) {
      gritStore.fetchGridData();
    }
  }, [])

  const isLeadDashboard = (recordType: string) => {
    return recordType?.toUpperCase() === LEAD_OBJECT_TYPE
  }

  const openMenu = (event, record) => {
    setAnchorEl(event.currentTarget);
    if (isLeadDashboard(cadence.recordType)) {
      cadenceStore.setSelectedProspectId(record.leadId)
    } else {
      cadenceStore.setSelectedProspectId(record.contactId)
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    cadenceStore.setSelectedProspectId(null);
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    gritStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    gritStore.setFilterModel(filterModel);
  };

  const handleCloseAddContactsModal = () => {
    cadenceStore.resetSelectedLeadList();
    setOpenAddContactsModal(false);
    cadenceStore.setIsAddingMoreProspectsActive(false)
    gritStore.setFilterModel(null);
  }

  const frameworkComponents = {
    actionCellRenderer: (params) => (
      <IconButton onClick={(e) => openMenu(e, params.node.data)}>
        <MoreVert color="primary" />
      </IconButton>
    ),
  };

  return (
    <>
      <div className={classes.peopleContainer}>
        {cadence?.metrics?.totalProspects > 0 ?
          <Box>
            <Box display="flex" flexDirection="row" gridGap="10px">
              {globalStore.userFeatures.backOfficeLead && isLeadDashboard(cadence.recordType) &&
                <Box>
                  <SelectField
                    value={cadenceStore.gridBusinessUnit}
                    options={businessUnitOptions}
                    onChange={value => {
                      cadenceStore.gridBusinessUnit = value
                      gritStore.fetchGridData();
                    }}
                    fullWidth
                    variant={'outlined'}
                    testId={'businessUnit'}
                    style={{ height: '40px', minWidth: "100px" }}
                  />
                </Box>
              }
            </Box>
          </Box>
          : (
            <>
              <Grid item xs={12}>
                <Box className={classes.noStepsContainer}>
                  <Typography variant="h6" color="textSecondary">
                    No prospects associated with the cadence yet
                  </Typography>
                </Box>
              </Grid>
            </>
          )
        }

        {cadence?.metrics?.totalProspects > 0 && (
          <DataGrid
            key={`testingid`}
            className={classes.container}
            headerCheckboxSelection={true}
            suppressRowClickSelection={true}
            frameworkComponents={frameworkComponents}
            columns={column}
            rows={gritStore.gridData.data.rows}
            onSortChanged={onSortChanged}
            onFilterChanged={onFilterChanged}
            rowHeight={45}
            checkBoxColumnPinned={true}
            enablePaginationSelectPage={true}
            showPagination={true}
            isLoading={gritStore.isLoading}
            showFilters={gritStore.showFilters}
            sortModel={gritStore.sortModel}
            filterModel={gritStore.filterModel}
            paginationData={gritStore.gridData.meta}
            setPageNumber={gritStore.setPageNumber}
          />
        )}
        <Menu
          id="menu-appbar"
          getContentAnchorEl={null}
          anchorEl={anchorEl}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={menuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem
            testId={`copy-template`}
            onClick={() => {
              cadenceStore.removeProspectFromPartialList()
              handleMenuClose()
            }}
          >
            <ListItemIcon>
              <FileCopy fontSize="small" />
            </ListItemIcon>
            <ListItemText >Remove Prospect</ListItemText>
          </MenuItem>
        </Menu>
      </div>
      <StandardDialog
        open={openAddContactsModal}
        title={"Associate Prospects"}
        maxWidth="md"
        handleClose={handleCloseAddContactsModal}
        dialogContent={<SelectCadenceListStepper handleCloseAddContactsModal={handleCloseAddContactsModal} gritStore={gritStore} />} />
    </>
  );
});
